import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from '../utils/shopifyApi';
import { toast } from 'sonner';
import { UpgradeModal } from '../components/UpgradeModal';
import { pickActiveStore, shouldBootstrap, resolveBootstrappedStore } from '../lib/storeBootstrap';
import { redirectTopFrame } from '../lib/topFrameRedirect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SAFE_BATCH_MAX_PRODUCTS = Number(process.env.REACT_APP_AI_BATCH_MAX_PRODUCTS || 50);
const SAFE_BATCH_DELAY_SECONDS = Number(process.env.REACT_APP_AI_BATCH_DELAY_SECONDS || 2);
const SAFE_BATCH_RETRY_ATTEMPTS = Number(process.env.REACT_APP_AI_BATCH_RETRY_ATTEMPTS || 2);
const NEXT_PLAN = { free: 'starter', starter: 'pro', pro: 'elite', elite: 'elite' };

const toArray = (value) => Array.isArray(value) ? value : [];
const extractProductsFromResponse = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.products)) return data.products;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};
const extractLogsFromResponse = (data) => Array.isArray(data?.logs) ? data.logs : toArray(data);
const extractSupportMessagesFromResponse = (data) => Array.isArray(data) ? data : toArray(data?.messages);

const AppContext = createContext(null);

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [settings, setSettings] = useState(null);
  const [logs, setLogs] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [catalogVersion, setCatalogVersion] = useState(0);
  const [syncProgress, setSyncProgress] = useState(null);
  const [analysisProgress, setAnalysisProgress] = useState(null);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [siteAudit, setSiteAudit] = useState(null);
  const [auditProgress, setAuditProgress] = useState(null);
  const [publicEnv, setPublicEnv] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(null);

  // Open the Shopify OAuth install flow for a given shop domain.
  // Used by the "Connetti Store Shopify" button on the empty-state UI.
  const connectShop = useCallback(async (rawShop) => {
    const shop = (rawShop || '').trim().toLowerCase();
    if (!shop) throw new Error('shop_domain missing');
    // Accept "mystore" or "mystore.myshopify.com".
    const normalized = shop.endsWith('.myshopify.com') ? shop : `${shop}.myshopify.com`;
    if (!/^[a-z0-9-]+\.myshopify\.com$/.test(normalized)) {
      throw new Error('Invalid domain. Example: mystore.myshopify.com');
    }
    // The consent screen cannot render inside Shopify's iframe, so this has to
    // leave it — through App Bridge, which is the only mechanism the browser
    // permits a cross-origin frame. Assigning window.top.location directly, as
    // this did, is refused with a SecurityError.
    //
    // Reached only from the empty-state button, i.e. always with a user
    // gesture behind it. Initialisation must never call this.
    const url = `${API}/auth/install?shop=${encodeURIComponent(normalized)}`;
    redirectTopFrame(url);
    return url;
  }, []);

  // Use ref to avoid circular dependency issues
  const storeRef = useRef(store);
  useEffect(() => {
    storeRef.current = store;
  }, [store]);

  const showUpgrade = useCallback((opts = {}) => {
    const currentPlan = (storeRef.current?.plan || 'free').toLowerCase();
    const defaultTarget = NEXT_PLAN[currentPlan] || 'starter';
    setUpgradeModal({
      reason: opts.reason || 'feature',
      target: opts.target || defaultTarget,
      feature: opts.feature || null,
    });
  }, []);

  const hideUpgrade = useCallback(() => setUpgradeModal(null), []);

  // ============ BASIC FETCH FUNCTIONS (no dependencies) ============
  
  const fetchProducts = useCallback(async (storeId, status = null) => {
    try {
      const params = { store_id: storeId, limit: 1000 };
      if (status) params.status = status;
      const res = await axios.get(`${API}/products`, { params });
      const normalizedProducts = extractProductsFromResponse(res.data);
      setProducts(normalizedProducts);
      return { ...(res.data || {}), products: normalizedProducts };
    } catch (error) {
      console.error('Failed to fetch products:', error);
      return { products: [], items: [], total: 0 };
    }
  }, []);

  const fetchUsage = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/usage/${storeId}`);
      setUsage(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch usage:', error);
      return null;
    }
  }, []);

  // Re-read the store document from the backend.
  //
  // `store` is otherwise written exactly once, by initializeStore on app
  // start, so anything derived from it — the plan cards, and PlanGate's
  // feature gating — keeps showing whatever was true at load time. That is
  // wrong the moment the merchant changes plan on Shopify's hosted page and
  // comes back: the backend has already reconciled the new plan, but the app
  // still gates on the old one until a full browser reload.
  //
  // Deliberately narrow: it touches `store` and nothing else. `loading` is
  // left alone so a refresh never blanks the page the merchant is looking at.
  const refreshStore = useCallback(async (storeId) => {
    if (!storeId) return null;
    try {
      const res = await axios.get(`${API}/stores/${storeId}`);
      setStore(res.data);
      return res.data;
    } catch (error) {
      // A failed refresh must leave the last known store in place: showing a
      // stale plan is recoverable, dropping the store is not.
      console.error('Failed to refresh store:', error);
      return null;
    }
  }, []);

  const fetchSettings = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/settings/${storeId}`);
      setSettings(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      return null;
    }
  }, []);

  const fetchLogs = useCallback(async (storeId, limit = 50) => {
    try {
      const res = await axios.get(`${API}/logs/${storeId}`, { params: { limit } });
      setLogs(res.data.logs);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      return { logs: [], total: 0 };
    }
  }, []);

  const fetchSupportMessages = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/support/${storeId}/messages`);
      setSupportMessages(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch support messages:', error);
      return [];
    }
  }, []);

  // ============ DASHBOARD SUMMARY ============

  const fetchDashboardSummary = useCallback(async (storeId, { fresh = false } = {}) => {
    try {
      const res = await axios.get(`${API}/dashboard/summary/${storeId}`, {
        params: fresh ? { fresh: 'true' } : undefined,
      });
      setDashboardSummary(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch dashboard summary:', error);
      return null;
    }
  }, []);

  // ============ SITE AUDIT ============

  const fetchSiteAudit = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/audit/site/${storeId}`);
      setSiteAudit(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch site audit:', error);
      return null;
    }
  }, []);

  const runSiteAudit = useCallback(async (storeId) => {
    try {
      setAuditProgress({ status: 'running', message: 'Starting site audit...', phase: 'queued' });
      await axios.post(`${API}/audit/site/${storeId}`);

      let attempts = 0;
      const maxAttempts = 90; // ~3 min
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempts += 1;
        const res = await axios.get(`${API}/audit/site/${storeId}`);
        const a = res.data || {};
        setSiteAudit(a);
        if (a.status === 'complete') {
          setAuditProgress({ status: 'complete', message: 'Audit complete', phase: 'done' });
          return a;
        }
        if (a.status === 'error') {
          const msg = a.error || 'Audit error';
          setAuditProgress({ status: 'error', message: msg, phase: 'error' });
          throw new Error(msg);
        }
        setAuditProgress({
          status: 'running',
          message: `Audit in progress (${a.phase || 'analysis'})...`,
          phase: a.phase || 'running',
        });
      }
      throw new Error('Audit timeout');
    } catch (error) {
      console.error('[AUDIT] Error:', error.response?.data || error.message);
      const msg = error.response?.data?.detail || error.message || 'Audit error';
      setAuditProgress({ status: 'error', message: msg });
      throw new Error(msg);
    }
  }, []);

  // ============ STORE INITIALIZATION ============

  /**
   * Create this shop's store record from its App Bridge session token.
   *
   * The only thing the app can prove about itself inside the Shopify iframe
   * is the session token, and that is enough: the backend verifies its
   * signature, audience, issuer, destination and expiry, takes the shop from
   * the verified `dest` claim alone, and exchanges the token for an offline
   * access token. Nothing here chooses which shop is bootstrapped — the
   * request carries no shop parameter at all, deliberately, so this cannot be
   * pointed at somebody else's store.
   *
   * Returns the store on success, or null. Callers must treat null as "still
   * no store", never as a failure to report to the merchant as an error: not
   * being embedded in Shopify Admin is a perfectly ordinary reason to get it.
   */
  const bootstrapFromSessionToken = useCallback(async () => {
    // No App Bridge means no session token to exchange. Asking anyway would
    // just be a guaranteed 401 on every boot of the standalone app.
    if (!shouldBootstrap(window.shopify, null)) return null;
    try {
      const r = await axios.post(`${API}/auth/session-token-exchange`);
      const after = await axios.get(`${API}/stores`).catch(() => ({ data: [] }));
      const fresh = resolveBootstrappedStore(r.data, after.data);
      if (!fresh) return null;

      console.log('[INIT] bootstrapped store:', fresh.shop_domain, 'has token:', !!fresh.has_access_token);
      // Populating context here is what makes the app usable immediately:
      // the merchant must not have to reload the browser to get past a state
      // they never caused.
      setStore(fresh);
      if (fresh.id && fresh.has_access_token) {
        axios.post(`${API}/billing/subscription/reconcile/${fresh.id}`).catch(() => {});
      }
      return fresh;
    } catch (_) {
      // A failed bootstrap is not fatal and not the merchant's problem to
      // read about: fall through to the app's ordinary no-store state.
      return null;
    }
  }, []);

  const initializeStore = useCallback(async () => {
    try {
      setLoading(true);
      
      // Check URL params for shop info (Shopify embedded passes ?shop=...)
      const urlParams = new URLSearchParams(window.location.search);
      const shopFromUrl = urlParams.get('shop');
      const storeIdFromUrl = urlParams.get('store_id');

      console.log('[INIT] URL params:', { shopFromUrl, storeIdFromUrl });

      // Fetch env + stores in parallel — /env is optional, never blocks boot
      // /stores now answers 401 when the request carries no verified App
      // Bridge session token — which is exactly the state of a merchant who
      // has opened the app before the install finished, since App Bridge
      // cannot mint a token for an app that is not installed yet. Treating
      // that as "no stores visible" rather than as a hard failure is what
      // keeps the auto-OAuth path below reachable: throwing here would land
      // in the outer catch and leave them staring at a blank app.
      const [envRes, storesRes] = await Promise.all([
        axios.get(`${API}/env`).catch(() => ({ data: null })),
        axios.get(`${API}/stores`).catch(() => ({ data: [] })),
      ]);
      setPublicEnv(envRes.data || null);
      const stores = Array.isArray(storesRes.data) ? storesRes.data : [];
      
      console.log('[INIT] Stores found:', stores.length);
      
      // An empty list is not a separate case: it lands on the same
      // "no store matched" path below, which starts OAuth when the URL tells
      // us which shop we are in. Returning early here skipped that.

      // Find the right store - prefer real stores over demo
      const activeStore = pickActiveStore(stores, {
        shop: shopFromUrl, storeId: storeIdFromUrl,
      });

      console.log('[INIT] Active store:', activeStore?.shop_domain, 'has token:', !!activeStore?.has_access_token);
      
      if (activeStore) {
        setStore(activeStore);
        // Shopify App Pricing sends no subscription webhooks, so a plan the
        // merchant changed or cancelled on Shopify's side is only discovered
        // by asking. Fire-and-forget: a billing check must never delay or
        // break app startup, and the scheduled job is the backstop.
        if (activeStore.id && activeStore.has_access_token) {
          axios.post(`${API}/billing/subscription/reconcile/${activeStore.id}`)
            .catch(() => {});
        }
        return activeStore;
      }

      // No store record matched. Under Shopify Managed Installation that is
      // what a brand-new merchant looks like: Shopify grants the scopes
      // declared in shopify.app.toml itself and launches the app embedded
      // with a session token, so the authorization-code redirect — the only
      // thing that used to create the record — never runs.
      //
      // This used to navigate the top frame straight to /auth/install. It
      // cannot work: "because embedded apps are rendered in the Shopify admin
      // inside an iframe, they don't have permission to manipulate the parent
      // browser window, including redirects"
      // (shopify.dev/docs/apps/launch/billing/shopify-app-pricing/redirect-plan-selection-page).
      // Attempted with no user gesture it raised SecurityError, which landed
      // in the catch below and left the merchant with an app that had failed
      // to initialise and a plan button that did nothing. That is what App
      // Review filmed.
      //
      // The supported path is to bootstrap from the session token instead:
      // the backend derives the shop from the token's verified `dest` claim
      // and exchanges the token for an offline access token. No navigation,
      // no consent screen, nothing for the merchant to do.
      const bootstrapped = await bootstrapFromSessionToken();
      if (bootstrapped) return bootstrapped;

      // Genuinely could not bootstrap. Return null so the app renders its
      // explicit "no store connected" state, which offers the merchant an
      // action. Never pretend initialisation succeeded, and never start a
      // top-frame navigation they did not ask for.
      console.warn('[INIT] no store for', shopFromUrl || '(unknown shop)', '— bootstrap unavailable');
      return null;
    } catch (error) {
      console.error('Failed to initialize store:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, [bootstrapFromSessionToken]);

  // ============ SHOPIFY SYNC ============

  const syncProducts = useCallback(async (storeId, fullResync = false) => {
    try {
      setSyncProgress({ status: 'syncing', message: 'Starting sync...', synced: 0, total: 0 });

      console.log('[SYNC] Starting sync for store:', storeId, 'full_resync=', fullResync);

      const startRes = await axios.post(
        `${API}/shopify/sync-products/${storeId}`,
        null,
        { params: { full_resync: fullResync } }
      );
      console.log('[SYNC] Started:', startRes.data);

      // Poll status every 2 seconds until complete/error/stale.
      // Additional safety: if the counter stops moving for ~3 minutes
      // we ask the server one last time (it will auto-reconcile via the
      // 5-min stale-detector) and exit instead of looping for 40 minutes.
      let attempts = 0;
      const maxAttempts = 1200; // ~40 minutes hard cap
      let lastSynced = -1;
      let stagnantPolls = 0;
      const STAGNANT_LIMIT = 90; // 90 polls * 2s = 3 minutes without progress
      while (attempts < maxAttempts) {
        await new Promise(r => setTimeout(r, 2000));
        attempts += 1;
        const statusRes = await axios.get(`${API}/shopify/sync-status/${storeId}`);
        const s = statusRes.data;
        const synced = s.sync_total_synced || 0;
        const total = s.sync_total_in_shopify || 0;
        const productsInDb = s.products_in_db || 0;
        const pct = total > 0 ? Math.round((synced / total) * 100) : 0;
        setSyncProgress({
          status: 'syncing',
          message: `Syncing: ${synced}${total ? ' / ' + total : ''} products (${pct}%)`,
          synced,
          total,
          progress: pct,
        });

        if (s.sync_status === 'complete') {
          setSyncProgress({
            status: 'complete',
            message: `Sync complete: ${synced} products`,
            synced,
            total,
            progress: 100,
          });
          const productsRes = await axios.get(`${API}/products`, { params: { store_id: storeId, limit: 1000 } });
          setProducts(extractProductsFromResponse(productsRes.data));
          try {
            const usageRes = await axios.get(`${API}/usage/${storeId}`);
            setUsage(usageRes.data);
          } catch (e) {
            console.error('Failed to fetch usage after sync:', e);
          }
          try {
            const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
            setDashboardSummary(sumRes.data);
          } catch (e) { /* ignore */ }
          return { products_synced: synced, total };
        }
        if (s.sync_status === 'products_limit_reached') {
          setSyncProgress({
            status: 'products_limit_reached',
            message: `Plan limit reached: ${synced} products synced out of ${total} in Shopify`,
            synced,
            total,
            progress: 100,
          });
          try {
            const productsRes = await axios.get(`${API}/products`, { params: { store_id: storeId, limit: 1000 } });
            setProducts(extractProductsFromResponse(productsRes.data));
          } catch (e) { /* ignore */ }
          try {
            const usageRes = await axios.get(`${API}/usage/${storeId}`);
            setUsage(usageRes.data);
          } catch (e) { /* ignore */ }
          try {
            const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
            setDashboardSummary(sumRes.data);
          } catch (e) { /* ignore */ }
          const currentPlan = (storeRef.current?.plan || 'free').toLowerCase();
          const target = NEXT_PLAN[currentPlan] || 'starter';
          setUpgradeModal({ reason: 'products', target, feature: null });
          return { products_synced: synced, total, limited: true };
        }
        if (s.sync_status === 'stale' || s.sync_status === 'idle') {
          // Server reconciled a dead worker; treat as soft-success using DB count.
          const finalCount = productsInDb || synced;
          setSyncProgress({
            status: 'complete',
            message: `Sync reconciled: ${finalCount} products in database`,
            synced: finalCount,
            total: total || finalCount,
            progress: 100,
          });
          try {
            const productsRes = await axios.get(`${API}/products`, { params: { store_id: storeId, limit: 1000 } });
            setProducts(extractProductsFromResponse(productsRes.data));
          } catch (e) { /* ignore */ }
          try {
            const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
            setDashboardSummary(sumRes.data);
          } catch (e) { /* ignore */ }
          return { products_synced: finalCount, total };
        }
        if (s.sync_status === 'error') {
          const msg = s.sync_error || 'Sync error';
          setSyncProgress({ status: 'error', message: msg, synced, total });
          throw new Error(msg);
        }

        if (synced === lastSynced) {
          stagnantPolls += 1;
        } else {
          stagnantPolls = 0;
          lastSynced = synced;
        }
        if (stagnantPolls >= STAGNANT_LIMIT) {
          console.warn('[SYNC] Counter stagnant; asking server to reconcile and stopping poll.');
          // One more call — the server's stale detector (>5 min idle) will flip
          // status to "stale" or "complete" on this read.
          try {
            const finalRes = await axios.get(`${API}/shopify/sync-status/${storeId}`);
            const f = finalRes.data;
            const finalCount = f.products_in_db || f.sync_total_synced || synced;
            setSyncProgress({
              status: 'complete',
              message: `Sync stopped; ${finalCount} products in database`,
              synced: finalCount,
              total: f.sync_total_in_shopify || total || finalCount,
              progress: 100,
            });
            try {
              const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
              setDashboardSummary(sumRes.data);
            } catch (e) { /* ignore */ }
            return { products_synced: finalCount, total: f.sync_total_in_shopify || total };
          } catch (e) {
            throw new Error('Sync stuck');
          }
        }
      }
      throw new Error('Sync timeout');
    } catch (error) {
      console.error('[SYNC] Error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Sync error';
      setSyncProgress({ status: 'error', message: errorMessage });
      throw new Error(errorMessage);
    }
  }, []);

  // Attaches to a sync already running server-side (e.g. after a browser
  // refresh) and runs the same polling loop without re-POSTing. Idempotent:
  // a guard ref prevents duplicate poll loops if called twice.
  const syncResumeRef = useRef({ running: false });
  const resumeSyncPolling = useCallback(async (storeId) => {
    if (!storeId || syncResumeRef.current.running) return;
    syncResumeRef.current.running = true;
    try {
      const MAX_SYNC_POLL = 600; // 30 min @ 3 s intervals
      let _syncPollCount = 0;
      while (_syncPollCount++ < MAX_SYNC_POLL) {
        const statusRes = await axios.get(`${API}/shopify/sync-status/${storeId}`);
        const s = statusRes.data || {};
        if (s.sync_status !== 'running') {
          // No active sync (or it finished/stale-detected by the server).
          setSyncProgress(null);
          break;
        }
        const synced = s.sync_total_synced || 0;
        const total = s.sync_total_in_shopify || 0;
        const pct = total > 0 ? Math.round((synced / total) * 100) : 0;
        setSyncProgress({
          status: 'syncing',
          message: `Syncing: ${synced}${total ? ' / ' + total : ''} products (${pct}%)`,
          synced, total, progress: pct,
        });
        await new Promise(r => setTimeout(r, 3000));
      }
      // After the loop exits, refresh the dashboard so the user sees the
      // final numbers without having to reload.
      try {
        const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`, { params: { fresh: 'true' } });
        setDashboardSummary(sumRes.data);
      } catch (e) { /* ignore */ }
    } catch (e) {
      console.error('[SYNC-RESUME] failed:', e);
      setSyncProgress(null);
    } finally {
      syncResumeRef.current.running = false;
    }
  }, []);

  const fetchSyncStatus = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/shopify/sync-status/${storeId}`);
      return res.data;
    } catch (error) {
      console.error('Failed to fetch sync status:', error);
      return null;
    }
  }, []);

  const pruneCatalog = useCallback(async (storeId) => {
    const res = await axios.post(`${API}/shopify/prune-catalog/${storeId}`);
    try {
      const productsRes = await axios.get(`${API}/products`, { params: { store_id: storeId, limit: 1000 } });
      setProducts(extractProductsFromResponse(productsRes.data));
    } catch (e) { /* ignore */ }
    try {
      const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
      setDashboardSummary(sumRes.data);
    } catch (e) { /* ignore */ }
    return res.data;
  }, []);

  const resetSyncState = useCallback(async (storeId) => {
    try {
      const res = await axios.post(`${API}/shopify/sync-reset/${storeId}`);
      setSyncProgress(null);
      try {
        const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`);
        setDashboardSummary(sumRes.data);
      } catch (e) { /* ignore */ }
      return res.data;
    } catch (error) {
      console.error('Failed to reset sync state:', error);
      throw error;
    }
  }, []);

  // ============ SEO ANALYSIS ============

  const analyzeStore = useCallback(async (storeId) => {
    const MAX_STALE_CYCLES = 30; // 30 × 2 s = 60 s — AI calls can be slow per product
    try {
      setAnalysisProgress({ status: 'analyzing', progress: 5, message: 'Starting SEO analysis...' });

      // Create or resume a persisted v2 job
      const startRes = await axios.post(`${API}/analyze/start/${storeId}`);
      const jobId = startRes.data?.id;
      if (!jobId) throw new Error('Job ID missing from server response');

      console.log('[ANALYZE-V2] Job started/resumed:', jobId);

      // Poll with anti-loop guard — stop only after MAX_STALE_CYCLES with zero movement
      // in processed_products AND the job is actually running (not pending/recovering).
      let staleCycles = 0;
      let lastProcessed = -1;

      while (true) {
        await new Promise(r => setTimeout(r, 2000));

        let s;
        try {
          const statusRes = await axios.get(`${API}/analyze/job-status/${jobId}`);
          s = statusRes.data || {};
        } catch (fetchErr) {
          staleCycles += 1;
          if (staleCycles >= MAX_STALE_CYCLES) {
            throw new Error(`Unable to reach server after ${MAX_STALE_CYCLES} consecutive attempts.`);
          }
          continue;
        }

        const done = s.processed_products || 0;
        const total = s.total_products || 0;
        const pct = total > 0 ? Math.round((done / total) * 100) : 0;

        // Only count stale when the worker is actively processing rows.
        // "pending" and "recovering" mean the worker hasn't started yet —
        // don't penalise them with stale cycles.
        if (s.status === 'running') {
          if (done === lastProcessed) {
            staleCycles += 1;
          } else {
            staleCycles = 0;
            lastProcessed = done;
          }
        } else {
          staleCycles = 0;
          lastProcessed = done;
        }

        setAnalysisProgress({
          status: 'analyzing',
          progress: pct,
          message: `Analysis: ${done}${total ? ' / ' + total : ''} products (${pct}%)`,
          done, total,
        });

        if (staleCycles >= MAX_STALE_CYCLES) {
          throw new Error(`No progress detected after ${MAX_STALE_CYCLES} cycles. Check server status.`);
        }

        if (s.status === 'completed') {
          const productsRes = await axios.get(`${API}/products`, { params: { store_id: storeId, limit: 1000 } });
          setProducts(extractProductsFromResponse(productsRes.data));
          try { const usageRes = await axios.get(`${API}/usage/${storeId}`); setUsage(usageRes.data); } catch (e) { /* ignore */ }
          try { const sumRes = await axios.get(`${API}/dashboard/summary/${storeId}`); setDashboardSummary(sumRes.data); } catch (e) { /* ignore */ }
          const avgScore = s.average_score ?? 0;
          setAnalysisProgress({
            status: 'complete',
            progress: 100,
            message: `Analysis complete: ${done} products, average score ${avgScore}/100`,
            results: s,
          });
          return s;
        }

        if (s.status === 'failed' || s.status === 'cancelled') {
          const msg = `Analysis ${s.status}`;
          setAnalysisProgress({ status: 'error', progress: pct, message: msg });
          throw new Error(msg);
        }
      }
    } catch (error) {
      console.error('[ANALYZE-V2] Error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Analysis error';
      setAnalysisProgress({ status: 'error', progress: 0, message: errorMessage });
      throw new Error(errorMessage);
    }
  }, []);

  const analyzeProduct = useCallback(async (productId) => {
    try {
      const res = await axios.post(`${API}/analyze/product/${productId}`);
      
      // Refresh products using ref to avoid dependency
      const currentStore = storeRef.current;
      if (currentStore) {
        const productsRes = await axios.get(`${API}/products`, { params: { store_id: currentStore.id, limit: 1000 } });
        setProducts(extractProductsFromResponse(productsRes.data));
      }
      
      return res.data;
    } catch (error) {
      console.error('Failed to analyze product:', error);
      throw error;
    }
  }, []);

  // ============ AI CONTENT GENERATION ============

  const generateContent = useCallback(async (productId, contentType, language = 'it', tone = 'professional') => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/ai/generate`, {
        product_id: productId,
        content_type: contentType,
        language,
        tone
      });

      // Patch only the affected product in the global state — no full reload.
      // The /ai/generate response already carries the new content, seo_score,
      // and seo_status, so we can keep every other page in sync without a
      // GET /products?limit=1000 round-trip.
      const fieldMap = {
        description:      'description',
        seo_title:        'seo_title',
        meta_description: 'meta_description',
        keywords:         'keywords',
      };
      const updatedField = fieldMap[contentType];
      if (updatedField && res.data?.content != null) {
        setProducts(prev => prev.map(p =>
          p.id === productId
            ? {
                ...p,
                [updatedField]: res.data.content,
                ...(res.data.seo_score  != null ? { seo_score:  res.data.seo_score  } : {}),
                ...(res.data.seo_status != null ? { seo_status: res.data.seo_status } : {}),
              }
            : p
        ));
      }

      return res.data;
    } catch (error) {
      console.error('Failed to generate content:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============ TRANSLATIONS ============

  const translateProduct = useCallback(async (productId, targetLanguage, fields) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/ai/translate`, {
        product_id: productId,
        target_language: targetLanguage,
        fields
      });
      
      const currentStore = storeRef.current;
      if (currentStore) {
        try {
          const usageRes = await axios.get(`${API}/usage/${currentStore.id}`);
          setUsage(usageRes.data);
        } catch (e) {
          console.error('Failed to fetch usage:', e);
        }
      }
      
      return res.data;
    } catch (error) {
      console.error('Failed to translate:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTranslations = useCallback(async (productId) => {
    try {
      const res = await axios.get(`${API}/translations/${productId}`);
      return res.data;
    } catch (error) {
      console.error('Failed to get translations:', error);
      return [];
    }
  }, []);

  const translateContent = useCallback(async (resourceId, targetLanguage, fields) => {
    try {
      const res = await axios.post(`${API}/content/${resourceId}/translate`, {
        target_language: targetLanguage,
        fields,
      });
      return res.data;
    } catch (error) {
      console.error('Failed to translate content resource:', error);
      throw error;
    }
  }, []);

  const fetchContentItems = useCallback(async (storeId, type, q = '') => {
    try {
      const params = { store_id: storeId, type, limit: 200 };
      if (q) params.q = q;
      const res = await axios.get(`${API}/content`, { params });
      return res.data?.items || [];
    } catch (error) {
      console.error('Failed to fetch content items:', error);
      return [];
    }
  }, []);

  const publishTranslation = useCallback(async (productId, translations) => {
    // 35 s matches the Shopify API timeout inside the endpoint; don't refetch
    // 1 000 products after each publish — it's expensive and floods HTTP/2.
    const res = await axios.post(
      `${API}/ai/publish-translation`,
      { product_id: productId, translations },
      { timeout: 35000 },
    );
    return res.data;
  }, []);

  const publishContentResource = useCallback(async (resourceId, draft = null) => {
    const payload = draft ? { draft } : {};
    const res = await axios.post(`${API}/content/${resourceId}/publish`, payload);
    return res.data;
  }, []);

  // ============ IMAGE OPTIMIZATION ============

  const optimizeImage = useCallback(async (productId, options = {}) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/images/optimize`, {
        product_id: productId,
        resize_to_square: options.resize ?? true,
        compress: options.compress ?? true,
        quality: options.quality ?? 85
      });
      
      const currentStore = storeRef.current;
      if (currentStore) {
        try {
          const usageRes = await axios.get(`${API}/usage/${currentStore.id}`);
          setUsage(usageRes.data);
        } catch (e) {
          console.error('Failed to fetch usage:', e);
        }
      }
      
      return res.data;
    } catch (error) {
      console.error('Failed to optimize image:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============ IMAGE BATCH (survives navigation + refresh) ============
  // The image optimize/apply loops used to live inside the Images page
  // component, so navigating away unmounted it and killed the loop. They
  // now run here in the provider (lives for the whole app session) and the
  // progress is mirrored to localStorage so a browser refresh can resume.
  const IMG_BATCH_KEY = 'ai_optimizer_image_batch';
  const APPLY_BATCH_KEY = 'ai_optimizer_apply_batch';

  const [imageBatch, setImageBatch] = useState(null);
  const [imageApplyBatch, setImageApplyBatch] = useState(null);
  const [readyDrafts, setReadyDrafts] = useState({}); // {productId: draft metadata}
  const imageBatchRef = useRef({ cancelled: false });
  const applyBatchRef = useRef({ cancelled: false });

  // Persist a lightweight snapshot (no heavy base64) so a refresh can resume.
  const persistImageBatch = (b) => {
    try {
      if (!b) { localStorage.removeItem(IMG_BATCH_KEY); return; }
      localStorage.setItem(IMG_BATCH_KEY, JSON.stringify({
        running: b.running, mode: b.mode, done: b.done, total: b.total,
        errors: b.errors, targets: b.targets, settings: b.settings,
        storeId: b.storeId, startedAt: b.startedAt, finishedAt: b.finishedAt,
        cancelled: b.cancelled,
      }));
    } catch (e) { /* quota / private mode - ignore */ }
  };
  const persistApplyBatch = (b) => {
    try {
      if (!b) { localStorage.removeItem(APPLY_BATCH_KEY); return; }
      localStorage.setItem(APPLY_BATCH_KEY, JSON.stringify(b));
    } catch (e) { /* ignore */ }
  };

  const refreshReadyDrafts = useCallback(async (storeId) => {
    if (!storeId) return {};
    try {
      const res = await axios.get(`${API}/images/pending-optimizations/${storeId}`);
      const map = {};
      (res.data?.items || []).forEach((d) => { map[d.product_id] = d; });
      setReadyDrafts(map);
      return map;
    } catch (e) {
      return {};
    }
  }, []);

  // Load the server-side "ready to apply" queue whenever the store changes,
  // so pending drafts show up even after a full browser refresh.
  useEffect(() => {
    if (store?.id) refreshReadyDrafts(store.id);
  }, [store?.id, refreshReadyDrafts]);

  const _runOptimizeLoop = useCallback(async (targets, settings, startIndex, seedErrors, seedDrafts, storeId) => {
    const errors = [...(seedErrors || [])];
    const drafts = { ...(seedDrafts || {}) };
    for (let i = startIndex; i < targets.length; i += 1) {
      if (imageBatchRef.current.cancelled) break;
      const product = targets[i];
      try {
        const res = await axios.post(`${API}/images/optimize`, {
          product_id: product.id,
          image_format: settings.image_format ?? '1:1',
          compress: settings.compress ?? true,
          quality: settings.quality ?? 85,
        });
        drafts[product.id] = {
          ...res.data,
          product_id: product.id,
          product_title: product.title,
          original_url: product.image_url,
          created_at: new Date().toISOString(),
          status: 'ready',
        };
        setReadyDrafts((m) => ({ ...m, [product.id]: drafts[product.id] }));
      } catch (e) {
        errors.push({
          id: product.id,
          title: product.title,
          message: e?.response?.data?.detail || e?.message || 'Optimization error',
        });
      }
      setImageBatch((b) => {
        const next = b ? { ...b, done: i + 1, errors: [...errors], drafts: { ...drafts } } : null;
        persistImageBatch(next);
        return next;
      });
    }
    imageBatchRef.current.running = false;
    setImageBatch((b) => {
      const next = b ? { ...b, running: false, finishedAt: Date.now() } : null;
      persistImageBatch(next);
      return next;
    });
    const currentStore = storeRef.current;
    if (currentStore) {
      try {
        const usageRes = await axios.get(`${API}/usage/${currentStore.id}`);
        setUsage(usageRes.data);
      } catch (e) { /* ignore */ }
    }
    return { drafts, errors };
  }, []);

  const startImageBatch = useCallback(async (products, settings = {}) => {
    if (!Array.isArray(products) || products.length === 0) return null;
    if (imageBatchRef.current?.running) return null;
    imageBatchRef.current = { cancelled: false, running: true };
    const targets = products.map((p) => ({ id: p.id, title: p.title, image_url: p.image_url }));
    const storeId = storeRef.current?.id || null;
    const initial = {
      running: true, mode: 'optimize', done: 0, total: targets.length,
      errors: [], drafts: {}, targets, settings, storeId, startedAt: Date.now(),
    };
    setImageBatch(initial);
    persistImageBatch(initial);
    return _runOptimizeLoop(targets, settings, 0, [], {}, storeId);
  }, [_runOptimizeLoop]);

  const cancelImageBatch = useCallback(() => {
    if (imageBatchRef.current) imageBatchRef.current.cancelled = true;
    setImageBatch((b) => {
      const next = b ? { ...b, running: false, cancelled: true } : null;
      persistImageBatch(next);
      return next;
    });
  }, []);

  const clearImageBatch = useCallback(() => {
    setImageBatch(null);
    persistImageBatch(null);
  }, []);

  // ----- APPLY batch -----
  const _runApplyLoop = useCallback(async (productIds, startIndex, seedErrors) => {
    const errors = [...(seedErrors || [])];
    for (let i = startIndex; i < productIds.length; i += 1) {
      if (applyBatchRef.current.cancelled) break;
      const pid = productIds[i];
      try {
        // Backend resolves the optimized image server-side from the pending
        // queue, so we only need to send the product id.
        await axios.post(`${API}/images/apply-optimized`, { product_id: pid });
        setReadyDrafts((m) => { const c = { ...m }; delete c[pid]; return c; });
      } catch (e) {
        errors.push({
          id: pid,
          message: e?.response?.data?.detail || e?.message || 'Publish error',
        });
      }
      setImageApplyBatch((b) => {
        const next = b ? { ...b, done: i + 1, errors: [...errors] } : null;
        persistApplyBatch(next);
        return next;
      });
    }
    applyBatchRef.current.running = false;
    setImageApplyBatch((b) => {
      const next = b ? { ...b, running: false, finishedAt: Date.now() } : null;
      persistApplyBatch(next);
      return next;
    });
    const currentStore = storeRef.current;
    if (currentStore) {
      try {
        const productsRes = await axios.get(`${API}/products`, { params: { store_id: currentStore.id, limit: 1000 } });
        setProducts(extractProductsFromResponse(productsRes.data));
      } catch (e) { /* ignore */ }
    }
    return { errors };
  }, []);

  const startApplyBatch = useCallback(async (productIds) => {
    const ids = (productIds || []).filter(Boolean);
    if (ids.length === 0) return null;
    if (applyBatchRef.current?.running) return null;
    applyBatchRef.current = { cancelled: false, running: true };
    const initial = {
      running: true, mode: 'apply', done: 0, total: ids.length,
      errors: [], productIds: ids, startedAt: Date.now(),
    };
    setImageApplyBatch(initial);
    persistApplyBatch(initial);
    return _runApplyLoop(ids, 0, []);
  }, [_runApplyLoop]);

  const cancelApplyBatch = useCallback(() => {
    if (applyBatchRef.current) applyBatchRef.current.cancelled = true;
    setImageApplyBatch((b) => {
      const next = b ? { ...b, running: false, cancelled: true } : null;
      persistApplyBatch(next);
      return next;
    });
  }, []);

  const clearApplyBatch = useCallback(() => {
    setImageApplyBatch(null);
    persistApplyBatch(null);
  }, []);

  // ----- CONTENT batch (collections / articles / pages) -----
  // Drives /content/{id}/generate (+ optional /publish) for many resources.
  // Lives in the provider so navigating away doesn't stop it, and is
  // mirrored to localStorage so a full refresh resumes from where it stopped.
  const CONTENT_BATCH_KEY = 'ai_optimizer_content_batch';
  const [contentBatch, setContentBatch] = useState(null);
  const contentBatchRef = useRef({ cancelled: false });

  const persistContentBatch = (b) => {
    try {
      if (!b) { localStorage.removeItem(CONTENT_BATCH_KEY); return; }
      localStorage.setItem(CONTENT_BATCH_KEY, JSON.stringify(b));
    } catch (e) { /* ignore */ }
  };

  const _runContentLoop = useCallback(async (state, startIndex, seedErrors) => {
    const { type, resourceIds, mode, fields, language, tone, autoPublish, storeId, triggerSource } = state;
    const errors = [...(seedErrors || [])];
    // Yield to React so the "running:true" state commits and the progress bar
    // renders before the loop starts (prevents batching race condition where
    // the loop finishes before the first paint if all products are skipped).
    await new Promise(r => setTimeout(r, 0));
    const totalItems = resourceIds.length; // capture once — never read from state/queue
    let stopReason = null;  // 'budget_exceeded' | 'stopped' | 'server_stop' | null
    let actualDone = startIndex;
    console.log('[AUTOPILOT] loop start — totalItems:', totalItems, 'startIndex:', startIndex);
    for (let i = startIndex; i < totalItems; i += 1) {
      if (contentBatchRef.current.cancelled) break;
      const rid = resourceIds[i];
      console.log('[AUTOPILOT] item', i + 1, '/', totalItems, '— id:', rid);
      let wasSkipped = false;
      try {
        if (mode === 'generate' || mode === 'both') {
          const generateUrl = type === 'products'
            ? `${API}/products/${rid}/generate`
            : `${API}/content/${rid}/generate`;
          const res = await axios.post(generateUrl, {
            fields: fields || ['seo_title', 'meta_description'],
            language: language || 'it',
            tone: tone || 'professionale',
            ...(triggerSource ? { trigger_source: triggerSource } : {}),
          });
          // Backend signals hard-stop (toggle turned OFF, budget exceeded)
          if (res.data?.status === 'stopped' || res.data?.status === 'budget_exceeded') {
            stopReason = res.data.status;
            actualDone = i; // this item was NOT processed
            console.warn('[AUTOPILOT] hard-stop at item', i + 1, ':', stopReason, res.data.errors?.[0]);
            // Increment progress BEFORE breaking so the break point is visible in the UI.
            setContentBatch((b) => {
              const next = b ? { ...b, done: i + 1, errors: [...errors] } : null;
              persistContentBatch(next);
              return next;
            });
            contentBatchRef.current.cancelled = true;
            break;
          }
          if (res.data?.status === 'skipped') wasSkipped = true;
        }
        if (!contentBatchRef.current.cancelled && (mode === 'publish' || mode === 'both' || (autoPublish && mode === 'generate'))) {
          const publishUrl = type === 'products'
            ? `${API}/products/${rid}/publish`
            : `${API}/content/${rid}/publish`;
          await axios.post(publishUrl);
        }
      } catch (e) {
        // 409 = backend hard-stop signal
        if (e?.response?.status === 409) {
          stopReason = 'server_stop';
          contentBatchRef.current.cancelled = true;
          break;
        }
        // 402 = credits exhausted or plan gate — stop the entire batch
        if (e?.response?.status === 402) {
          stopReason = 'credits_exhausted';
          contentBatchRef.current.cancelled = true;
          errors.push({ id: rid, message: e?.response?.data?.detail || 'credits_exhausted' });
          break;
        }
        errors.push({
          id: rid,
          message: e?.response?.data?.detail || e?.message || 'Error',
        });
      }
      actualDone = i + 1;
      setContentBatch((b) => {
        const next = b ? { ...b, done: i + 1, errors: [...errors] } : null;
        persistContentBatch(next);
        return next;
      });
      // Brief pause: lets React render each progress tick and prevents the
      // browser extension (MetaMask) from flooding the console with stream
      // reset warnings caused by rapid back-to-back HTTP requests.
      // Skipped items (anti-reprocessing) bypass the delay to speed up runs.
      if (i < totalItems - 1 && !contentBatchRef.current.cancelled && !wasSkipped) {
        await new Promise(r => setTimeout(r, 120));
      }
    }
    contentBatchRef.current.running = false;
    console.log('[AUTOPILOT] loop end — cancelled:', contentBatchRef.current.cancelled, 'stopReason:', stopReason, 'actualDone:', actualDone);
    // AWAIT the stop call so it is confirmed before _runContentLoop returns.
    // Fire-and-forget caused a race: the previous run's /stop could arrive at
    // the backend AFTER the next run's /start reset stop_requested=False,
    // poisoning the new run's first generate → {status:'stopped'} after 1 item.
    if (storeId) {
      await axios.post(`${API}/autopilot/stop/${storeId}`).catch(() => {});
    }
    setContentBatch((b) => {
      const next = b ? { ...b, running: false, finishedAt: Date.now() } : null;
      persistContentBatch(next);
      return next;
    });
    return { errors, stopReason, done: actualDone };
  }, []);

  const startContentBatch = useCallback(async ({ type, resourceIds, mode = 'generate',
                                                 fields, language = 'it', tone = 'professionale',
                                                 autoPublish = false, storeId, triggerSource } = {}) => {
    if (!Array.isArray(resourceIds) || resourceIds.length === 0) return null;
    if (contentBatchRef.current?.running) return null;
    contentBatchRef.current = { cancelled: false, running: true };
    const sid = storeId || storeRef.current?.id;
    // AWAIT the start call so the backend resets stop_requested=False BEFORE the
    // loop fires its first generate. Fire-and-forget here caused a race where the
    // previous run's /stop arrived after the new /start, leaving stop_requested=True
    // when the first /generate hit the backend → "stopped" response after 1 item.
    if (sid) {
      try {
        await axios.post(`${API}/autopilot/start/${sid}`, { mode, total: resourceIds.length });
      } catch (startErr) {
        // 402 = plan gate blocked (Free plan, feature not available).
        // Propagate: stop the batch immediately and let the caller handle the upgrade prompt.
        if (startErr?.response?.status === 402) {
          contentBatchRef.current = null;
          return { errors: [{ id: 'plan', message: startErr.response.data?.detail }], stopReason: 'plan_gate', done: 0 };
        }
        // Other errors (network, 5xx): log and continue — autopilot/start failure is non-fatal
        // for resetting stop_requested, but the batch can still proceed.
      }
    }
    const initial = {
      running: true, mode, type,
      resourceIds, fields, language, tone, autoPublish,
      storeId: sid,
      triggerSource,
      done: 0, total: resourceIds.length,
      errors: [],
      startedAt: Date.now(),
    };
    setContentBatch(initial);
    persistContentBatch(initial);
    return _runContentLoop(initial, 0, []);
  }, [_runContentLoop]);

  const cancelContentBatch = useCallback((reason = 'user_cancel') => {
    if (contentBatchRef.current) contentBatchRef.current.cancelled = true;
    const sid = storeRef.current?.id;
    if (sid) {
      axios.post(`${API}/autopilot/stop/${sid}`).catch(() => {});
    }
    setContentBatch((b) => {
      const next = b ? { ...b, running: false, cancelled: true, cancelReason: reason } : null;
      persistContentBatch(next);
      return next;
    });
  }, []);

  const clearContentBatch = useCallback(() => {
    setContentBatch(null);
    persistContentBatch(null);
  }, []);

  // Auto-resume an interrupted content batch on first mount.
  const contentBatchRehydratedRef = useRef(false);
  useEffect(() => {
    if (contentBatchRehydratedRef.current) return;
    contentBatchRehydratedRef.current = true;
    try {
      const raw = localStorage.getItem(CONTENT_BATCH_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved && saved.running && Array.isArray(saved.resourceIds) && saved.done < saved.total) {
        contentBatchRef.current = { cancelled: false, running: true };
        setContentBatch(saved);
        _runContentLoop(saved, saved.done || 0, saved.errors || []);
      } else if (saved && !saved.running) {
        setContentBatch(saved);
      }
    } catch (e) { /* ignore */ }
  }, [_runContentLoop]);

  // On first mount: rehydrate any interrupted batch and resume it.
  const batchRehydratedRef = useRef(false);
  useEffect(() => {
    if (batchRehydratedRef.current) return;
    batchRehydratedRef.current = true;
    // Resume optimize batch
    try {
      const raw = localStorage.getItem(IMG_BATCH_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.running && Array.isArray(saved.targets) && saved.done < saved.total) {
          imageBatchRef.current = { cancelled: false, running: true };
          const resumed = { ...saved, drafts: saved.drafts || {} };
          setImageBatch(resumed);
          _runOptimizeLoop(saved.targets, saved.settings || {}, saved.done || 0, saved.errors || [], {}, saved.storeId);
        } else if (saved && !saved.running) {
          setImageBatch(saved);
        }
      }
    } catch (e) { /* ignore */ }
    // Resume apply batch
    try {
      const raw = localStorage.getItem(APPLY_BATCH_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.running && Array.isArray(saved.productIds) && saved.done < saved.total) {
          applyBatchRef.current = { cancelled: false, running: true };
          setImageApplyBatch(saved);
          _runApplyLoop(saved.productIds, saved.done || 0, saved.errors || []);
        } else if (saved && !saved.running) {
          setImageApplyBatch(saved);
        }
      }
    } catch (e) { /* ignore */ }
  }, [_runOptimizeLoop, _runApplyLoop]);

  // ============ BATCH OPERATIONS ============

  const processBatch = useCallback(async (productIds, operation, options = {}) => {
    try {
      setLoading(true);
      const safeProductIds = Array.isArray(productIds)
        ? productIds.slice(0, options.max_products || SAFE_BATCH_MAX_PRODUCTS)
        : [];
      const safeOptions = {
        ...options,
        max_products: options.max_products || SAFE_BATCH_MAX_PRODUCTS,
        delay_seconds: options.delay_seconds ?? SAFE_BATCH_DELAY_SECONDS,
        retry_attempts: options.retry_attempts ?? SAFE_BATCH_RETRY_ATTEMPTS,
        safe_mode: true,
      };
      // Use batch/v2/create (gated, monitored) instead of legacy batch/process
      const res = await axios.post(`${API}/batch/v2/create`, {
        store_id: storeRef.current?.id || options.store_id || '',
        product_ids: safeProductIds,
        operation_type: operation,
        selected_fields: options.selected_fields || null,
        options: safeOptions,
      });
      return res.data;
    } catch (error) {
      console.error('Failed to process batch:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getBatchStatus = useCallback(async (batchId) => {
    try {
      const res = await axios.get(`${API}/batch/${batchId}`);
      return res.data;
    } catch (error) {
      console.error('Failed to get batch status:', error);
      return null;
    }
  }, []);

  // ============ BATCH V2 ============

  const createBatchJob = useCallback(async ({ storeId, productIds, operationType, selectedFields, options }) => {
    const res = await axios.post(`${API}/batch/v2/create`, {
      store_id:       storeId,
      product_ids:    productIds,
      operation_type: operationType,
      selected_fields: selectedFields,
      options,
    });
    return res.data;
  }, []);

  const getBatchJobStatus = useCallback(async (jobId) => {
    try {
      const res = await axios.get(`${API}/batch/v2/status/${jobId}`);
      return res.data;
    } catch { return null; }
  }, []);

  const getActiveBatchJob = useCallback(async (storeId) => {
    try {
      const res = await axios.get(`${API}/batch/v2/active/${storeId}`);
      return res.data || null;
    } catch { return null; }
  }, []);

  const getBatchHistory = useCallback(async (storeId, limit = 50) => {
    try {
      const res = await axios.get(`${API}/batch/v2/history/${storeId}`, { params: { limit } });
      return res.data || [];
    } catch { return []; }
  }, []);

  const cancelBatchJob = useCallback(async (jobId) => {
    try {
      const res = await axios.post(`${API}/batch/v2/cancel/${jobId}`);
      return res.data;
    } catch { return null; }
  }, []);

  // ============ SETTINGS ============

  const updateSettings = useCallback(async (storeId, newSettings) => {
    try {
      const res = await axios.put(`${API}/settings/${storeId}`, newSettings);
      setSettings(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to update settings:', error);
      throw error;
    }
  }, []);

  // ============ AUTOPILOT SCHEDULER ============

  // Hard-stop running batch when user disables auto-optimize
  const prevAutoOptimizeRef = useRef(null);
  useEffect(() => {
    const curr = settings?.ai_engine?.auto_optimize;
    if (prevAutoOptimizeRef.current === true && curr === false) {
      // Transition ON→OFF: immediately cancel any running loop
      if (contentBatchRef.current?.running) {
        cancelContentBatch('toggle_off');
      }
    }
    prevAutoOptimizeRef.current = curr;
  }, [settings?.ai_engine?.auto_optimize, cancelContentBatch]);

  const autopilotTimerRef = useRef(null);

  useEffect(() => {
    if (autopilotTimerRef.current) {
      clearTimeout(autopilotTimerRef.current);
      autopilotTimerRef.current = null;
    }
    const ai = settings?.ai_engine;
    if (!ai?.auto_optimize || !store?.id) return;

    const delayMs = ai.auto_frequency === 'realtime' ? 60_000
                  : ai.auto_frequency === 'hourly'   ? 3_600_000
                  :                                    86_400_000; // daily

    const runAutopilot = async () => {
      try {
        // Single source of truth guard: never stack the background scheduler on
        // top of an already-running content batch ("Esegui Ora"). Running both
        // systems against the same products is the real duplicate-execution risk.
        if (contentBatchRef.current?.running) {
          console.log('[AUTOPILOT-SCHED] skipped — content batch already running');
          autopilotTimerRef.current = setTimeout(runAutopilot, delayMs);
          return;
        }
        const threshold = ai.auto_threshold ?? 70;
        const scope     = ai.auto_scope || ['products'];
        const storeId   = storeRef.current?.id;
        if (!storeId) return;
        if (scope.includes('products')) {
          const res = await axios.get(`${API}/products`, {
            params: { store_id: storeId, seo_status: 'needs_improvement', limit: 50 },
          });
          const ids = (res.data?.products || [])
            .filter(p => (p.seo_score ?? 0) < threshold)
            .map(p => p.id);
          if (ids.length > 0) {
            // startContentBatch is defined later but captured via closure at call time
            // Use the ref pattern to avoid stale closure
            const tone = storeRef.current ? settings?.tone : 'professionale';
            const lang = settings?.default_language || 'it';
            await axios.post(`${API}/batch/v2/create`, {
              store_id: storeId,
              product_ids: ids,
              operation_type: 'generate_content',
              selected_fields: ['seo_title', 'meta_description'],
              options: { language: lang, tone: tone || 'professionale', mode: 'generate' },
            });
          }
        }
      } catch (e) {
        console.error('[AUTOPILOT]', e);
      }
      // Schedule next run
      autopilotTimerRef.current = setTimeout(runAutopilot, delayMs);
    };

    autopilotTimerRef.current = setTimeout(runAutopilot, delayMs);
    return () => {
      if (autopilotTimerRef.current) clearTimeout(autopilotTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings?.ai_engine?.auto_optimize, settings?.ai_engine?.auto_frequency, store?.id]);

  // ============ SUPPORT ============

  const sendSupportMessage = useCallback(async (storeId, message) => {
    try {
      const res = await axios.post(`${API}/support/messages`, {
        store_id: storeId,
        message,
        sender: 'user'
      });
      
      // Refresh messages
      try {
        const messagesRes = await axios.get(`${API}/support/${storeId}/messages`);
        setSupportMessages(messagesRes.data);
      } catch (e) {
        console.error('Failed to fetch messages:', e);
      }
      
      return res.data;
    } catch (error) {
      console.error('Failed to send message:', error);
      throw error;
    }
  }, []);

  // ============ UTILITY ============

  const clearProducts = useCallback(async (storeId) => {
    try {
      await axios.delete(`${API}/store/${storeId}/clear-products`);
      setProducts([]);
    } catch (error) {
      console.error('Failed to clear products:', error);
      throw error;
    }
  }, []);

  // ============ EFFECTS ============

  // Initialize on mount
  useEffect(() => {
    initializeStore();
  }, [initializeStore]);

  // Fetch data when store changes
  useEffect(() => {
    if (!store?.id) return;
    console.log('[EFFECT] Store changed, fetching data for:', store.shop_domain);
    fetchProducts(store.id);
    fetchUsage(store.id);
    fetchSettings(store.id);
    fetchLogs(store.id);
    fetchSupportMessages(store.id);
    fetchDashboardSummary(store.id);
    fetchSiteAudit(store.id);
    // If a Shopify product sync is already running server-side (e.g. the
    // user refreshed the page mid-sync), pick the polling back up so the
    // progress banner reappears and the dashboard refreshes when it ends.
    resumeSyncPolling(store.id);
    // Only re-run when the store identity actually changes — not on every
    // mutation of the store object (avoids 7 parallel re-fetches per nav).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  // Global Axios interceptor: catch HTTP 402 (credits_exhausted) from any component.
  // Registered once on mount, ejected on unmount. Uses storeRef to read current
  // plan without adding reactive dependencies.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        if (err?.response?.status === 402) {
          const detail = err.response.data || {};
          const currentPlan = (storeRef.current?.plan || 'free').toLowerCase();
          const target = NEXT_PLAN[currentPlan] || 'starter';
          if (detail.error === 'credits_exhausted') {
            setUpgradeModal({ reason: 'credits', target, feature: null });
          } else if (detail.error === 'products_limit_reached') {
            setUpgradeModal({ reason: 'products', target, feature: null });
          } else if (detail.error === 'feature_not_available') {
            setUpgradeModal({ reason: 'feature', target, feature: detail.feature || null });
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const incrementCatalogVersion = useCallback(() => setCatalogVersion(v => v + 1), []);

  // ============ CONTEXT VALUE ============

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({
    store,
    products,
    loading,
    usage,
    settings,
    logs,
    supportMessages,
    selectedProducts,
    setSelectedProducts,
    catalogVersion,
    incrementCatalogVersion,
    syncProgress,
    analysisProgress,
    dashboardSummary,
    siteAudit,
    auditProgress,
    fetchDashboardSummary,
    fetchSiteAudit,
    runSiteAudit,
    fetchProducts,
    fetchUsage,
    refreshStore,
    bootstrapFromSessionToken,
    fetchSettings,
    updateSettings,
    fetchLogs,
    generateContent,
    translateProduct,
    optimizeImage,
    imageBatch,
    startImageBatch,
    cancelImageBatch,
    clearImageBatch,
    imageApplyBatch,
    startApplyBatch,
    cancelApplyBatch,
    clearApplyBatch,
    readyDrafts,
    refreshReadyDrafts,
    contentBatch,
    startContentBatch,
    cancelContentBatch,
    clearContentBatch,
    processBatch,
    getBatchStatus,
    createBatchJob,
    getBatchJobStatus,
    getActiveBatchJob,
    getBatchHistory,
    cancelBatchJob,
    fetchSupportMessages,
    sendSupportMessage,
    getTranslations,
    translateContent,
    fetchContentItems,
    publishTranslation,
    publishContentResource,
    initializeStore,
    publicEnv,
    connectShop,
    syncProducts,
    fetchSyncStatus,
    resumeSyncPolling,
    resetSyncState,
    pruneCatalog,
    analyzeStore,
    analyzeProduct,
    clearProducts,
    upgradeModal,
    showUpgrade,
    hideUpgrade,
  }), [store, products, loading, usage, settings, logs, supportMessages, selectedProducts,
    setSelectedProducts, catalogVersion, incrementCatalogVersion, syncProgress, analysisProgress,
    dashboardSummary, siteAudit, auditProgress, fetchDashboardSummary, fetchSiteAudit, runSiteAudit,
    fetchProducts, fetchUsage, refreshStore, bootstrapFromSessionToken,
    fetchSettings, updateSettings, fetchLogs, generateContent, translateProduct,
    optimizeImage, imageBatch, startImageBatch, cancelImageBatch, clearImageBatch, imageApplyBatch,
    startApplyBatch, cancelApplyBatch, clearApplyBatch, readyDrafts, refreshReadyDrafts, contentBatch,
    startContentBatch, cancelContentBatch, clearContentBatch, processBatch, getBatchStatus, createBatchJob,
    getBatchJobStatus, getActiveBatchJob, getBatchHistory, cancelBatchJob, fetchSupportMessages,
    sendSupportMessage, getTranslations, translateContent, fetchContentItems, publishTranslation,
    publishContentResource, initializeStore, publicEnv, connectShop, syncProducts, fetchSyncStatus,
    resumeSyncPolling, resetSyncState, pruneCatalog, analyzeStore, analyzeProduct, clearProducts,
    upgradeModal, showUpgrade, hideUpgrade]);

  return (
    <AppContext.Provider value={value}>
      {children}
      <UpgradeModal
        open={!!upgradeModal}
        reason={upgradeModal?.reason}
        target={upgradeModal?.target}
        feature={upgradeModal?.feature}
        onClose={hideUpgrade}
      />
    </AppContext.Provider>
  );
};

export default AppContext;
